import {
  Typography,
  Box,
  Paper,
  Slider,
  IconButton,
  Button as MuiButton,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { getPinsDict } from 'application/routes/config/utils/tempsUtils';
import React, { useState, useEffect } from 'react';

import FormSection from 'application/components/FormSection/FormSection';
import ProtectedSection from 'application/components/FormSection/ProtectedSection';
import useOrgData from 'application/hooks/useOrgData';
import {
  SecondaryButton,
  SectionTitle,
  StyledButton,
  SwitchLabel,
  SwitchDescription,
  StyledTextField,
} from './configurationForms/styledFormComponents';
import {
  StyledSelect,
  StyledMenuItem,
} from '../CompanyInformation/StyledSelect';
import _ from 'lodash';
import { GamifyToast } from 'application/common/CustomToasts';
import * as types from 'application/_types/types.type';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { FormSelectPerson } from 'application/common/FormInputs';
import { gql } from 'graphql-tag';
import AppointmentSMSTrigger from './configurationForms/AppointmentSMSTrigger';
import featureFlag from 'application/common/featureFlag';
import TabOrderConfig from './TabOrderConfig';
const { v4: uuidv4 } = require('uuid');

type Feature =
  | 'schedule'
  | 'route'
  | 'deal'
  | 'feed'
  | 'recording'
  | 'webInterface';

const formatTime = (time: number): string => {
  let hour = Math.floor(time / 60);
  const min = Math.floor(time % 60);
  let ampm = 'PM';
  if (hour < 12) {
    ampm = 'AM';
  } else if (hour > 12) {
    hour = hour - 12;
  }
  if (hour === 0) {
    hour = 12;
  }
  const hourStr = hour < 10 ? `0${hour}` : `${hour}`;
  const minStr = min < 10 ? `0${min}` : `${min}`;
  return `${hourStr}:${minStr} ${ampm}`;
};

const defaultScheduleTemp = {
  default: [
    {
      days: [0, 1, 2, 3, 4, 5],
      defaultMeetingLength: 90,
      endTime: 1260,
      maximumMeetingLength: 600,
      minimumMeetingLength: 30,
      minutesBetweenMeetings: 0,
      startTime: 540,
    },
  ],
};

const createOrgHistory = /* GraphQL */ `
  mutation CreateOrgHistory($input: CreateOrgHistoryInput!) {
    createOrgHistory(input: $input) {
      id
      orgID
      object
      createdAt
    }
  }
`;

const listUsersQuery = gql`
  query ListUsersByOrg(
    $orgID: ID
    $email: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $nextToken: String
  ) {
    listUsersByOrg(
      orgID: $orgID
      email: $email
      sortDirection: $sortDirection
      filter: { status: { ne: "deleted" } }
      limit: 150
      nextToken: $nextToken
    ) {
      items {
        id
        username
        email
        name
      }
      nextToken
    }
  }
`;

interface FeaturesConfigProps {
  selectedCRM: types.StageCategoryPartial | null;
  categories: types.StageCategoryPartial[];
}
export default function FeaturesConfig({
  selectedCRM,
  categories,
}: FeaturesConfigProps) {
  const [featuresConfig, setFeaturesConfig] = useState<any>(null);
  const [defaultSchedule, setDefaultSchedule] = useState<any>(null);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const { orgData, updateOrgData } = useOrgData();
  const [allUsers, setAllUsers] = useState<any>([]);
  const [damageReportsEnabled, setDamageReportsEnabled] =
    useState<boolean>(false);
  const [damageReportsLogo, setDamageReportsLogo] = useState<string>('');
  const [damageReportsHeroImages, setDamageReportsHeroImages] = useState<
    string[]
  >([]);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState<boolean>(false);
  const [tabOrder, setTabOrder] = useState<any>(null);
  const [summaryCRM, setSummaryCRM] =
    useState<types.StageCategoryPartial | null>(null);
  const activeSummaryCRM = summaryCRM || selectedCRM;

  const pinIcons = getPinsDict();
  const availableDispositions = (activeSummaryCRM?.dispositions ?? []).filter(
    (d) => !d.isDeleted
  );
  const crmDispositionIds: string[] = (() => {
    const perCrm = featuresConfig?.route?.areaSummaryDispositions;
    if (perCrm && typeof perCrm === 'object' && activeSummaryCRM?.id) {
      const ids = perCrm[activeSummaryCRM.id];
      if (Array.isArray(ids)) return ids;
    }
    return [];
  })();
  const selectedSummaryDispositions = availableDispositions.filter((d) =>
    crmDispositionIds.includes(d.id || '')
  );

  const handleResetOrg = () => {
    setFeaturesConfig(_.cloneDeep(orgData?.configParms?.features));
    setDefaultSchedule(_.cloneDeep(orgData?.configParms?.defaultSchedule));
    const damageReports = orgData?.configParms?.damageReports || {};
    setDamageReportsEnabled(damageReports.enabled || false);
    setDamageReportsLogo(damageReports.logo || '');
    setDamageReportsHeroImages(damageReports.heroImages || []);
    setTabOrder(_.cloneDeep(orgData?.configParms?.tabOrder) || null);
  };

  const getUsers = async () => {
    const usersList = [];
    let ourNextToken = null;
    do {
      const usersRes: any = await API.graphql(
        graphqlOperation(listUsersQuery, {
          orgID: orgData?.id,
          nextToken: ourNextToken,
        })
      );
      usersList.push(...usersRes.data.listUsersByOrg.items);
      ourNextToken = usersRes.data.listUsersByOrg.nextToken;
    } while (ourNextToken);
    setAllUsers(usersList);
  };

  const uploadLogo = async (file: File) => {
    try {
      setUploadingLogo(true);
      const fileName = `damage-reports/logo/${orgData?.id}/${Date.now()}_${
        file.name
      }`;
      await Storage.put(fileName, file, {
        contentType: file.type,
      });
      setDamageReportsLogo(fileName);
    } catch (error) {
      console.error('Error uploading logo:', error);
      GamifyToast.error('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const uploadHeroImage = async (file: File) => {
    try {
      setUploadingHeroImage(true);
      const fileName = `damage-reports/hero-images/${
        orgData?.id
      }/${Date.now()}_${file.name}`;
      await Storage.put(fileName, file, {
        contentType: file.type,
      });
      setDamageReportsHeroImages((prev) => [...prev, fileName]);
    } catch (error) {
      console.error('Error uploading hero image:', error);
      GamifyToast.error('Error uploading hero image');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const removeHeroImage = (index: number) => {
    setDamageReportsHeroImages((prev) => prev.filter((_, i) => i !== index));
  };
  const handleFeatureKeyUpdate = (
    feature: Feature,
    key: string,
    value: boolean | string | number | string[] | Record<string, string[]>
  ) => {
    setFeaturesConfig((prev: any) => ({
      ...prev,
      [feature]: {
        ...prev?.[feature],
        [key]: value,
      },
    }));
  };

  const CLUSTERER_DEFAULTS = {
    minZoom: 0,
    maxZoom: 14,
    minPoints: 6,
    radius: 60,
  };

  const handleClustererUpdate = (key: string, value: number) => {
    setFeaturesConfig((prev: any) => {
      const cur = prev?.route?.clusterer ?? {};
      return {
        ...prev,
        route: {
          ...prev?.route,
          clusterer: {
            minZoom: cur.minZoom ?? CLUSTERER_DEFAULTS.minZoom,
            maxZoom: cur.maxZoom ?? CLUSTERER_DEFAULTS.maxZoom,
            minPoints: cur.minPoints ?? CLUSTERER_DEFAULTS.minPoints,
            radius: cur.radius ?? CLUSTERER_DEFAULTS.radius,
            [key]: value,
          },
        },
      };
    });
  };

  const clustererMinZoom =
    featuresConfig?.route?.clusterer?.minZoom ?? CLUSTERER_DEFAULTS.minZoom;
  const clustererMaxZoom =
    featuresConfig?.route?.clusterer?.maxZoom ?? CLUSTERER_DEFAULTS.maxZoom;
  const clusterValidationError = clustererMinZoom >= clustererMaxZoom;
  const handleSaveUpdates = async () => {
    console.log(orgData);

    const newConfigParms = {
      ...orgData?.configParms,
      features: featuresConfig,
      defaultSchedule,
      tabOrder: tabOrder || undefined,
      damageReports: {
        enabled: damageReportsEnabled,
        logo: damageReportsLogo,
        heroImages: damageReportsHeroImages,
      },
    };
    const newOrgInput = {
      ...orgData,
      configParms: JSON.stringify(newConfigParms),
    };
    const historyItem = {
      id: uuidv4(),
      orgID: orgData?.id,
      createdAt: new Date().toISOString(),
      object: JSON.stringify(newConfigParms),
    };
    console.log(historyItem);
    // if (featuresConfig?.class !== 2) return;
    try {
      await API.graphql(
        graphqlOperation(createOrgHistory, { input: historyItem })
      );
      await updateOrgData(newOrgInput);
      // Update global.appSettings.features so NavBar reflects changes immediately
      if ((global as any).appSettings) {
        (global as any).appSettings.features = { ...featuresConfig };
        (global as any).appSettings.tabOrder = tabOrder
          ? JSON.parse(JSON.stringify(tabOrder))
          : undefined;
      }
      GamifyToast.success('Features configuration updated successfully');
    } catch (error) {
      console.error('Error creating org history', error);
      GamifyToast.error('Error creating org history');
    }
  };

  useEffect(() => {
    if (orgData) {
      setFeaturesConfig(_.cloneDeep(orgData?.configParms?.features));
      setDefaultSchedule(
        _.cloneDeep(orgData?.configParms?.defaultSchedule) || null
      );
      const damageReports = orgData?.configParms?.damageReports || {};
      setDamageReportsEnabled(damageReports.enabled || false);
      setDamageReportsLogo(damageReports.logo || '');
      setDamageReportsHeroImages(damageReports.heroImages || []);
      setTabOrder(_.cloneDeep(orgData?.configParms?.tabOrder) || null);
    }
  }, [orgData]);

  useEffect(() => {
    if (featuresConfig?.schedule?.autoSchedule && !allUsers.length) {
      getUsers();
    }
    const originalDamageReports = orgData?.configParms?.damageReports || {};
    const currentDamageReports = {
      enabled: damageReportsEnabled,
      logo: damageReportsLogo,
      heroImages: damageReportsHeroImages,
    };
    setHasChanged(
      !_.isEqual(featuresConfig, orgData?.configParms?.features) ||
        !_.isEqual(defaultSchedule, orgData?.configParms?.defaultSchedule) ||
        !_.isEqual(currentDamageReports, originalDamageReports) ||
        !_.isEqual(tabOrder, orgData?.configParms?.tabOrder || null)
    );
  }, [
    featuresConfig,
    defaultSchedule,
    orgData,
    damageReportsEnabled,
    damageReportsLogo,
    damageReportsHeroImages,
    tabOrder,
  ]);

  const handleDefaultScheduleUpdate = (field: string, value: any) => {
    if (!defaultSchedule?.default?.[0]) {
      setDefaultSchedule(defaultScheduleTemp);
      return;
    }
    setDefaultSchedule((prev: any) => ({
      default: [{ ...prev.default[0], [field]: value }],
    }));
  };

  const handleDayToggle = (dayIndex: number) => {
    if (!defaultSchedule?.default?.[0]) {
      setDefaultSchedule(defaultScheduleTemp);
      return;
    }
    const temp = [...defaultSchedule.default[0].days];
    if (temp.includes(dayIndex)) {
      const ind = temp.indexOf(dayIndex);
      temp.splice(ind, 1);
    } else {
      temp.push(dayIndex);
      temp.sort();
    }
    handleDefaultScheduleUpdate('days', temp);
  };

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.custom?.background1,
        borderRadius: '16px',
        marginBottom: 2,
      })}
    >
      <Typography
        sx={(theme) => ({
          fontWeight: 780,
          fontSize: 20,
          color: theme.custom?.text,
        })}
      >
        Features Configuration
      </Typography>
      <Typography
        sx={(theme) => ({
          fontWeight: 400,
          fontSize: 15,
          color: theme.custom?.textLight,
          marginBottom: 2,
        })}
      >
        {`Configure feature settings for your organization.`}
      </Typography>
      {/* Route Features */}
      <ProtectedSection
        message={'Contact support to enable the canvassing feature.'}
        locked={!featuresConfig?.route?.enabled}
      >
        <SectionTitle>Canvassing</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Wander Mode Enabled</SwitchLabel>
              <SwitchDescription>
                When enabled, the user will be able to wander around the map and
                drop pins.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.wanderMode}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('route', 'wanderMode', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Disable Search</SwitchLabel>
              <SwitchDescription>
                When enabled, the user will not be able to search for specific
                addresses.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.disableSearch}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('route', 'disableSearch', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Only creator can change disposition</SwitchLabel>
              <SwitchDescription>
                Only the creator of the pin can change the disposition of the
                pin.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.onlyCreatorCanChangeDisposition}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate(
                  'route',
                  'onlyCreatorCanChangeDisposition',
                  checked
                );
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Only creator can Delete disposition</SwitchLabel>
              <SwitchDescription>
                Only the creator of the pin can delete the pin.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.onlyCreatorCanDelete}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate(
                  'route',
                  'onlyCreatorCanDelete',
                  checked
                );
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Only creator can edit a deal</SwitchLabel>
              <SwitchDescription>
                Only the creator of the pin can edit the pin&apos;s deal.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.onlyCreatorCanEdit}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('route', 'onlyCreatorCanEdit', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Only creator can view details</SwitchLabel>
              <SwitchDescription>
                Only the creator of the pin can view the pin&apos;s details.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.onlyCreatorCanViewDetails}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate(
                  'route',
                  'onlyCreatorCanViewDetails',
                  checked
                );
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Rep Locator</SwitchLabel>
              <SwitchDescription>
                Control which user roles can see other reps on the map.
              </SwitchDescription>
            </Box>
            <StyledSelect
              value={featuresConfig?.route?.repLocator || 'none'}
              onChange={(e) => {
                handleFeatureKeyUpdate(
                  'route',
                  'repLocator',
                  e.target.value as string
                );
              }}
              sx={{ minWidth: 150 }}
            >
              {['None', 'User', 'Manager', 'Admin'].map((el) => (
                <StyledMenuItem key={el} value={el.toLowerCase()}>
                  <Typography>{el}</Typography>
                </StyledMenuItem>
              ))}
            </StyledSelect>
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '40%' }}>
              <SwitchLabel>HailTrace App Key</SwitchLabel>
              <SwitchDescription>
                The App Key for HailTrace integration.
              </SwitchDescription>
            </Box>
            <StyledTextField
              placeholder="Enter HailTrace App Key"
              value={featuresConfig?.route?.hailTraceAppKey || ''}
              onChange={(e) => {
                handleFeatureKeyUpdate(
                  'route',
                  'hailTraceAppKey',
                  e.target.value
                );
              }}
              sx={{ minWidth: 280 }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Area Disposition Summary</SwitchLabel>
              <SwitchDescription>
                When enabled, tapping an area on the map will show a summary of
                disposition counts for pins within that area.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.areaSummaryEnabled}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('route', 'areaSummaryEnabled', checked);
              }}
            />
          </FormSection>
          {featuresConfig?.route?.areaSummaryEnabled && (
            <FormSection
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ maxWidth: '40%' }}>
                <SwitchLabel>Dispositions to Display</SwitchLabel>
                <SwitchDescription>
                  Select a CRM, then choose which dispositions to include in the
                  area summary. Leave empty to show all.
                </SwitchDescription>
                <StyledSelect
                  value={activeSummaryCRM?.id || ''}
                  onChange={(e) => {
                    const crm = categories.find((c) => c.id === e.target.value);
                    if (crm) setSummaryCRM(crm as types.StageCategoryPartial);
                  }}
                  size="small"
                  sx={{ mt: 1, minWidth: 180 }}
                >
                  {categories.map((cat) => (
                    <StyledMenuItem key={cat.id} value={cat.id}>
                      {cat.title}
                    </StyledMenuItem>
                  ))}
                </StyledSelect>
              </Box>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={availableDispositions}
                value={selectedSummaryDispositions}
                getOptionLabel={(option) => option.title || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, newValue) => {
                  if (!activeSummaryCRM?.id) return;
                  const ids = newValue
                    .map((d) => d.id)
                    .filter((id): id is string => !!id);
                  const existing =
                    featuresConfig?.route?.areaSummaryDispositions || {};
                  handleFeatureKeyUpdate('route', 'areaSummaryDispositions', {
                    ...existing,
                    [activeSummaryCRM.id]: ids,
                  });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const iconSrc =
                      pinIcons[option.iconName as keyof typeof pinIcons];
                    return (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.title}
                        size="small"
                        sx={(theme) => ({
                          backgroundColor: theme.custom?.background2,
                          color: theme.custom?.text,
                          '& .MuiChip-deleteIcon': {
                            color: theme.custom?.textLight,
                          },
                        })}
                        avatar={
                          iconSrc ? (
                            <img
                              src={iconSrc}
                              alt={option.title}
                              style={{ width: 20, height: 20 }}
                            />
                          ) : undefined
                        }
                      />
                    );
                  })
                }
                renderOption={(props, option) => {
                  const iconSrc =
                    pinIcons[option.iconName as keyof typeof pinIcons];
                  return (
                    <li {...props} key={option.id}>
                      {iconSrc && (
                        <img
                          src={iconSrc}
                          alt={option.title}
                          style={{
                            width: 28,
                            height: 28,
                            marginRight: 8,
                          }}
                        />
                      )}
                      <Typography>{option.title}</Typography>
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedSummaryDispositions.length
                        ? ''
                        : 'All dispositions (default)'
                    }
                    size="small"
                  />
                )}
                sx={(theme) => ({
                  minWidth: 320,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.custom?.background1,
                    color: theme.custom?.text,
                    '& fieldset': {
                      borderColor: theme.custom?.stroke,
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.custom?.textLight,
                    opacity: 1,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.custom?.textLight,
                  },
                })}
                componentsProps={{
                  paper: {
                    sx: (theme) => ({
                      backgroundColor: theme.custom?.background1,
                      color: theme.custom?.text,
                    }),
                  },
                }}
              />
            </FormSection>
          )}
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Show Distance From Door</SwitchLabel>
              <SwitchDescription>
                When enabled, the distance from the pin to the door will be
                displayed on the pin screen in the Door Data section.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.showDistanceFromDoor}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate(
                  'route',
                  'showDistanceFromDoor',
                  checked
                );
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Enable Multi-Pin</SwitchLabel>
              <SwitchDescription>
                When enabled, users can create multiple pins on the same
                house/address. Note: when enabled, only clicking directly on a
                pin will open its info.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.route?.enableMultiPin}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('route', 'enableMultiPin', checked);
              }}
            />
          </FormSection>
          <FormSection>
            <SectionTitle>Pin Clustering</SectionTitle>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: '2%',
                rowGap: 3,
                mt: 2,
              }}
            >
              {/* Cluster Start Level */}
              <Box sx={{ width: { xs: '100%', md: '48%' }, minWidth: 200 }}>
                <Box
                  sx={(theme) => ({
                    px: 2,
                    py: 1,
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#03252E',
                        })}
                      >
                        Cluster Start Level
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight || '#666666',
                        })}
                      >
                        How far out clustering begins
                      </Typography>
                    </Box>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 14,
                        fontWeight: 600,
                        color: theme.custom?.text || '#03252E',
                      })}
                    >
                      {featuresConfig?.route?.clusterer?.minZoom ??
                        CLUSTERER_DEFAULTS.minZoom}
                    </Typography>
                  </Box>
                  <Slider
                    min={0}
                    max={10}
                    step={1}
                    value={
                      featuresConfig?.route?.clusterer?.minZoom ??
                      CLUSTERER_DEFAULTS.minZoom
                    }
                    onChange={(_, newVal) => {
                      handleClustererUpdate('minZoom', newVal as number);
                    }}
                    sx={(theme) => ({
                      color: theme.custom?.primary || '#FF6A00',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.custom?.primary || '#FF6A00',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: theme.custom?.stroke || '#E0E0E0',
                      },
                    })}
                  />
                </Box>
              </Box>

              {/* Cluster Stop Level */}
              <Box sx={{ width: { xs: '100%', md: '48%' }, minWidth: 200 }}>
                <Box
                  sx={(theme) => ({
                    px: 2,
                    py: 1,
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#03252E',
                        })}
                      >
                        Cluster Stop Level
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight || '#666666',
                        })}
                      >
                        Zoom level where clusters break apart into individual
                        pins
                      </Typography>
                    </Box>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 14,
                        fontWeight: 600,
                        color: theme.custom?.text || '#03252E',
                      })}
                    >
                      {featuresConfig?.route?.clusterer?.maxZoom ??
                        CLUSTERER_DEFAULTS.maxZoom}
                    </Typography>
                  </Box>
                  <Slider
                    min={10}
                    max={20}
                    step={1}
                    value={
                      featuresConfig?.route?.clusterer?.maxZoom ??
                      CLUSTERER_DEFAULTS.maxZoom
                    }
                    onChange={(_, newVal) => {
                      handleClustererUpdate('maxZoom', newVal as number);
                    }}
                    sx={(theme) => ({
                      color: theme.custom?.primary || '#FF6A00',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.custom?.primary || '#FF6A00',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: theme.custom?.stroke || '#E0E0E0',
                      },
                    })}
                  />
                </Box>
              </Box>

              {/* Min. Pins to Group */}
              <Box sx={{ width: { xs: '100%', md: '48%' }, minWidth: 200 }}>
                <Box
                  sx={(theme) => ({
                    px: 2,
                    py: 1,
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#03252E',
                        })}
                      >
                        Min. Pins to Group
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight || '#666666',
                        })}
                      >
                        Minimum nearby pins needed to form a cluster
                      </Typography>
                    </Box>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 14,
                        fontWeight: 600,
                        color: theme.custom?.text || '#03252E',
                      })}
                    >
                      {featuresConfig?.route?.clusterer?.minPoints ??
                        CLUSTERER_DEFAULTS.minPoints}
                    </Typography>
                  </Box>
                  <Slider
                    min={2}
                    max={20}
                    step={1}
                    value={
                      featuresConfig?.route?.clusterer?.minPoints ??
                      CLUSTERER_DEFAULTS.minPoints
                    }
                    onChange={(_, newVal) => {
                      handleClustererUpdate('minPoints', newVal as number);
                    }}
                    sx={(theme) => ({
                      color: theme.custom?.primary || '#FF6A00',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.custom?.primary || '#FF6A00',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: theme.custom?.stroke || '#E0E0E0',
                      },
                    })}
                  />
                </Box>
              </Box>

              {/* Grouping Distance (px) */}
              <Box sx={{ width: { xs: '100%', md: '48%' }, minWidth: 200 }}>
                <Box
                  sx={(theme) => ({
                    px: 2,
                    py: 1,
                    backgroundColor: theme.custom?.background2 || '#F5F5F5',
                    borderRadius: '12px',
                  })}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 14,
                          fontWeight: 600,
                          color: theme.custom?.text || '#03252E',
                        })}
                      >
                        Grouping Distance (px)
                      </Typography>
                      <Typography
                        sx={(theme) => ({
                          fontSize: 12,
                          color: theme.custom?.textLight || '#666666',
                        })}
                      >
                        How close pins must be to cluster together
                      </Typography>
                    </Box>
                    <Typography
                      sx={(theme) => ({
                        fontSize: 14,
                        fontWeight: 600,
                        color: theme.custom?.text || '#03252E',
                      })}
                    >
                      {featuresConfig?.route?.clusterer?.radius ??
                        CLUSTERER_DEFAULTS.radius}
                    </Typography>
                  </Box>
                  <Slider
                    min={20}
                    max={150}
                    step={1}
                    value={
                      featuresConfig?.route?.clusterer?.radius ??
                      CLUSTERER_DEFAULTS.radius
                    }
                    onChange={(_, newVal) => {
                      handleClustererUpdate('radius', newVal as number);
                    }}
                    sx={(theme) => ({
                      color: theme.custom?.primary || '#FF6A00',
                      '& .MuiSlider-thumb': {
                        '&:hover, &.Mui-focusVisible': {
                          boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                        },
                      },
                      '& .MuiSlider-track': {
                        backgroundColor: theme.custom?.primary || '#FF6A00',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: theme.custom?.stroke || '#E0E0E0',
                      },
                    })}
                  />
                </Box>
              </Box>
            </Box>

            {clusterValidationError && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  fontSize: 13,
                  fontWeight: 500,
                  mt: 1,
                }}
              >
                Cluster Start Level must be lower than Cluster Stop Level
              </Typography>
            )}
          </FormSection>
        </Box>
      </ProtectedSection>
      {/* Schedule Features */}
      <ProtectedSection
        sx={{ marginTop: 4 }}
        message={'Contact support to enable the schedule feature.'}
        locked={!featuresConfig?.schedule?.enabled}
      >
        <SectionTitle>Schedule</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormSection>
            <SectionTitle>Default Schedule</SectionTitle>
            {!defaultSchedule?.default && (
              <Box sx={{ mt: 2 }}>
                <StyledButton
                  onClick={() => setDefaultSchedule(defaultScheduleTemp)}
                >
                  Create default schedule
                </StyledButton>
              </Box>
            )}
            {defaultSchedule?.default && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Available Days */}
                <Box>
                  <SwitchLabel>Available Days</SwitchLabel>
                  <Box
                    sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}
                  >
                    {[
                      'Monday',
                      'Tuesday',
                      'Wednesday',
                      'Thursday',
                      'Friday',
                      'Saturday',
                      'Sunday',
                    ].map((day, index) => {
                      const isSelected =
                        defaultSchedule.default[0].days.includes(index);
                      return (
                        <Box
                          key={index}
                          onClick={() => handleDayToggle(index)}
                          sx={(theme) => ({
                            borderRadius: '5px',
                            padding: '5px 10px',
                            cursor: 'pointer',
                            border: `1px solid ${
                              isSelected
                                ? theme.custom?.primary || '#FF6A00'
                                : theme.custom?.stroke || '#E0E0E0'
                            }`,
                            backgroundColor: isSelected
                              ? theme.custom?.primary || '#FF6A00'
                              : theme.custom?.background3 || '#EEEEEE',
                            color: isSelected
                              ? theme.custom?.textCTA || '#FFFFFF'
                              : theme.custom?.textLight || '#666666',
                            '&:hover': {
                              border: `1px solid ${
                                theme.custom?.primaryLight || '#FAC8B2'
                              }`,
                            },
                          })}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                          >
                            {day}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                {/* Meeting Length Sliders */}
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    columnGap: '2%', // horizontal gap
                    rowGap: 2,
                    flexWrap: 'wrap',
                    // justifyContent: 'space-between',
                  }}
                >
                  {/* Default Meeting Length */}
                  <Box sx={{ width: { xs: '100%', md: '24%' }, minWidth: 200 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          Default Meeting Length
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {defaultSchedule.default[0].defaultMeetingLength} min
                        </Typography>
                      </Box>
                      <Slider
                        min={15}
                        max={360}
                        step={15}
                        value={
                          defaultSchedule.default[0].defaultMeetingLength || 0
                        }
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate(
                            'defaultMeetingLength',
                            newVal
                          );
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>

                  {/* Minimum Meeting Length */}
                  <Box sx={{ width: { xs: '100%', md: '24%' }, minWidth: 200 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          Minimum Meeting Length
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {defaultSchedule.default[0].minimumMeetingLength} min
                        </Typography>
                      </Box>
                      <Slider
                        min={15}
                        max={1000}
                        step={15}
                        value={
                          defaultSchedule.default[0].minimumMeetingLength || 0
                        }
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate(
                            'minimumMeetingLength',
                            newVal
                          );
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>

                  {/* Maximum Meeting Length */}
                  <Box sx={{ width: { xs: '100%', md: '24%' }, minWidth: 200 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          Maximum Meeting Length
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {defaultSchedule.default[0].maximumMeetingLength} min
                        </Typography>
                      </Box>
                      <Slider
                        min={15}
                        max={1000}
                        step={30}
                        value={
                          defaultSchedule.default[0].maximumMeetingLength || 0
                        }
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate(
                            'maximumMeetingLength',
                            newVal
                          );
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>

                  {/* Minutes Between Meetings */}
                  <Box sx={{ width: { xs: '100%', md: '24%' }, minWidth: 200 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          Minutes Between Meetings
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {defaultSchedule.default[0].minutesBetweenMeetings}{' '}
                          min
                        </Typography>
                      </Box>
                      <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={
                          defaultSchedule.default[0].minutesBetweenMeetings || 0
                        }
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate(
                            'minutesBetweenMeetings',
                            newVal
                          );
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Start Time and End Time */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10%',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Start Time */}
                  <Box sx={{ width: { xs: '100%', md: '45%' }, minWidth: 250 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          Start Time
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {formatTime(defaultSchedule.default[0].startTime)}
                        </Typography>
                      </Box>
                      <Slider
                        min={0}
                        max={1439}
                        step={5}
                        value={defaultSchedule.default[0].startTime || 0}
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate('startTime', newVal);
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>

                  {/* End Time */}
                  <Box sx={{ width: { xs: '100%', md: '45%' }, minWidth: 250 }}>
                    <Box
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        backgroundColor: theme.custom?.background2 || '#F5F5F5',
                        borderRadius: '12px',
                      })}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'center',
                          mb: 1,
                        }}
                      >
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          End Time
                        </Typography>
                        <Typography
                          sx={(theme) => ({
                            fontSize: 14,
                            fontWeight: 600,
                            color: theme.custom?.text || '#03252E',
                          })}
                        >
                          {formatTime(defaultSchedule.default[0].endTime)}
                        </Typography>
                      </Box>
                      <Slider
                        min={0}
                        max={1439}
                        step={5}
                        value={defaultSchedule.default[0].endTime || 0}
                        onChange={(e, newVal) => {
                          handleDefaultScheduleUpdate('endTime', newVal);
                        }}
                        sx={(theme) => ({
                          color: theme.custom?.primary || '#FF6A00',
                          '& .MuiSlider-thumb': {
                            '&:hover, &.Mui-focusVisible': {
                              boxShadow: `0px 0px 0px 8px rgba(255, 106, 0, 0.16)`,
                            },
                          },
                          '& .MuiSlider-track': {
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: theme.custom?.stroke || '#E0E0E0',
                          },
                        })}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* use round robin */}
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Use Round Robin</SwitchLabel>
              <SwitchDescription>
                When enabled, the scheduler will use round robin to assign
                appointments to users.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.schedule?.useRoundRobin}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('schedule', 'useRoundRobin', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Use Fixed Blocks</SwitchLabel>
              <SwitchDescription>
                When enabled, appointments will be scheduled in fixed time
                blocks instead of flexible slots.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.schedule?.useFixedBlocks ?? false}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('schedule', 'useFixedBlocks', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Self-Scheduling Only</SwitchLabel>
              <SwitchDescription>
                When enabled, users can only schedule appointments for
                themselves, regardless of their role (setter, closer, or
                manager).
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.schedule?.userSchedulesForSelf ?? false}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate(
                  'schedule',
                  'userSchedulesForSelf',
                  checked
                );
              }}
            />
          </FormSection>
          <FormSection>
            <SectionTitle>Auto Schedule</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormSection>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>Use Auto Schedule</SwitchLabel>
                    <SwitchDescription>
                      When enabled, the selected scheduler will receive all
                      appointments to schedule for users.
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={featuresConfig?.schedule?.autoSchedule}
                    onChange={(checked: boolean) => {
                      handleFeatureKeyUpdate(
                        'schedule',
                        'autoSchedule',
                        checked
                      );
                    }}
                  />
                </Box>
                {featuresConfig?.schedule?.autoSchedule && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      mt: 2,
                    }}
                  >
                    {/* <SectionTitle>Scheduler</SectionTitle> */}
                    {/* select scheduler */}
                    <FormSelectPerson
                      title="Scheduler"
                      value={featuresConfig?.schedule?.schedulerID}
                      fieldSetter={(value: string) => {
                        handleFeatureKeyUpdate(
                          'schedule',
                          'schedulerID',
                          value
                        );
                      }}
                      people={allUsers.map((user: any) => ({
                        value: user.id,
                        label: user.name,
                      }))}
                    />
                  </Box>
                )}
              </FormSection>
            </Box>
          </FormSection>
          <FormSection>
            <SectionTitle>Appointment Notifications</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormSection>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ maxWidth: '80%' }}>
                    <SwitchLabel>
                      Enable Appointment SMS Notifications
                    </SwitchLabel>
                    <SwitchDescription>
                      Configure SMS notifications for users and customers.
                    </SwitchDescription>
                  </Box>
                  <ToggleSwitch
                    checked={
                      featuresConfig?.schedule?.appointmentNotifications
                        ?.enabled
                    }
                    onChange={(checked: boolean) => {
                      console.log(checked);
                      setFeaturesConfig((prev: any) => ({
                        ...prev,
                        schedule: {
                          ...prev.schedule,
                          appointmentNotifications: {
                            ...prev.schedule?.appointmentNotifications,
                            enabled: checked,
                          },
                        },
                      }));
                    }}
                  />
                </Box>
              </FormSection>
              {featuresConfig?.schedule?.appointmentNotifications?.enabled && (
                <Box>
                  <SectionTitle>SMS Notifications</SectionTitle>
                  {featuresConfig?.schedule?.appointmentNotifications?.triggers?.map(
                    (trigger: any, index: number) => (
                      <AppointmentSMSTrigger
                        key={index}
                        trigger={trigger}
                        index={index}
                        smsVariables={
                          selectedCRM?.flexAttributes?.config?.deal
                            ?.flexFields || []
                        }
                        setFeaturesConfig={setFeaturesConfig}
                        featuresConfig={featuresConfig}
                      />
                    )
                  )}
                </Box>
              )}
            </Box>
          </FormSection>
        </Box>
      </ProtectedSection>
      {/* Recording Features */}
      {/* <FormSection sx={{ marginTop: 4 }}> */}
      <ProtectedSection
        sx={{ marginTop: 4 }}
        message={'Contact support to enable the recording feature.'}
        locked={!featureFlag('recording')}
      >
        <SectionTitle>Recording </SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Auto Recording</SwitchLabel>
              <SwitchDescription>
                When enabled, the recording will be automatically started when
                the user presses a house on the map.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.recording?.autoRecording}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('recording', 'autoRecording', checked);
              }}
            />
          </FormSection>

          {/* Recording Permissions Section */}
          <FormSection>
            <SectionTitle>Recording Permissions</SectionTitle>
            <SwitchDescription sx={{ mb: 2 }}>
              Control who can listen to recordings within your organization.
            </SwitchDescription>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Team Recording Access</SwitchLabel>
                  <SwitchDescription>
                    When enabled, team members can listen to recordings from
                    other members of their team.
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={featuresConfig?.recording?.teamCanHearTeam ?? false}
                  onChange={(checked: boolean) => {
                    handleFeatureKeyUpdate(
                      'recording',
                      'teamCanHearTeam',
                      checked
                    );
                  }}
                />
              </FormSection>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Admin Recording Access</SwitchLabel>
                  <SwitchDescription>
                    When enabled, admins can listen to all recordings in the
                    organization.
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={featuresConfig?.recording?.adminCanHearAll ?? true}
                  onChange={(checked: boolean) => {
                    handleFeatureKeyUpdate(
                      'recording',
                      'adminCanHearAll',
                      checked
                    );
                  }}
                />
              </FormSection>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Recording Visibility</SwitchLabel>
                  <SwitchDescription>
                    Control the default visibility level for recordings.
                  </SwitchDescription>
                </Box>
                <StyledSelect
                  value={
                    featuresConfig?.recording?.defaultVisibility || 'creator'
                  }
                  onChange={(e) => {
                    handleFeatureKeyUpdate(
                      'recording',
                      'defaultVisibility',
                      e.target.value as string
                    );
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <StyledMenuItem value="creator">
                    <Typography>Creator Only</Typography>
                  </StyledMenuItem>
                  <StyledMenuItem value="team">
                    <Typography>Team Members</Typography>
                  </StyledMenuItem>
                  <StyledMenuItem value="everyone">
                    <Typography>Everyone</Typography>
                  </StyledMenuItem>
                </StyledSelect>
              </FormSection>
            </Box>
          </FormSection>
        </Box>
      </ProtectedSection>
      {/* </FormSection> */}
      {/* Web Interface Configuration */}
      <FormSection sx={{ marginTop: 4 }}>
        <SectionTitle>Web Interface</SectionTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Hide Products</SwitchLabel>
              <SwitchDescription>
                When enabled, the Products tab will be hidden from the web
                interface navigation.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.webInterface?.hideProducts}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('webInterface', 'hideProducts', checked);
              }}
            />
          </FormSection>
          <FormSection
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ maxWidth: '80%' }}>
              <SwitchLabel>Hide Feed</SwitchLabel>
              <SwitchDescription>
                When enabled, the Feed tab will be hidden from the web interface
                navigation.
              </SwitchDescription>
            </Box>
            <ToggleSwitch
              checked={featuresConfig?.webInterface?.hideFeed}
              onChange={(checked: boolean) => {
                handleFeatureKeyUpdate('webInterface', 'hideFeed', checked);
              }}
            />
          </FormSection>
          {featuresConfig?.schedule?.enabled && (
            <FormSection
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ maxWidth: '80%' }}>
                <SwitchLabel>Disable Scheduling</SwitchLabel>
                <SwitchDescription>
                  When enabled, the Scheduling tab will be hidden from the web
                  interface navigation.
                </SwitchDescription>
              </Box>
              <ToggleSwitch
                checked={featuresConfig?.webInterface?.disableScheduling}
                onChange={(checked: boolean) => {
                  handleFeatureKeyUpdate(
                    'webInterface',
                    'disableScheduling',
                    checked
                  );
                }}
              />
            </FormSection>
          )}
        </Box>
      </FormSection>
      {/* Damage Reports Configuration */}
      <FormSection sx={{ marginTop: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <SectionTitle>Damage Reports</SectionTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SwitchLabel>Enabled</SwitchLabel>
            <ToggleSwitch
              checked={damageReportsEnabled}
              onChange={(checked: boolean) => {
                setDamageReportsEnabled(checked);
              }}
            />
          </Box>
        </Box>

        {damageReportsEnabled && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Logo Upload */}
            <FormSection>
              <SwitchLabel sx={{ mb: 2 }}>Logo</SwitchLabel>
              <Box
                sx={(theme) => ({
                  border: `2px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
                  borderRadius: '12px',
                  padding: 3,
                  textAlign: 'center',
                  backgroundColor: theme.custom?.background2 || '#F5F5F5',
                })}
              >
                {damageReportsLogo ? (
                  <Box>
                    <Box
                      component="img"
                      src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                        damageReportsLogo
                      )}`}
                      alt="Logo"
                      sx={{
                        width: 120,
                        height: 120,
                        objectFit: 'contain',
                        marginBottom: 2,
                        borderRadius: '8px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            uploadLogo(file);
                          }
                        }}
                        style={{ display: 'none' }}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <MuiButton
                          variant="outlined"
                          component="span"
                          disabled={uploadingLogo}
                          sx={{ mr: 1 }}
                        >
                          {uploadingLogo ? 'Uploading...' : 'Replace Logo'}
                        </MuiButton>
                      </label>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={(theme) => ({
                        width: 120,
                        height: 120,
                        border: `2px dashed ${
                          theme.custom?.stroke || '#E0E0E0'
                        }`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 15px auto',
                        backgroundColor: theme.custom?.background3 || '#EEEEEE',
                      })}
                    >
                      <Typography
                        sx={(theme) => ({
                          color: theme.custom?.textLight || '#999',
                          fontSize: '0.9rem',
                        })}
                      >
                        No Logo
                      </Typography>
                    </Box>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          uploadLogo(file);
                        }
                      }}
                      style={{ display: 'none' }}
                      id="logo-upload"
                    />
                    <label htmlFor="logo-upload">
                      <MuiButton
                        variant="outlined"
                        component="span"
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </MuiButton>
                    </label>
                  </Box>
                )}
              </Box>
            </FormSection>

            {/* Hero Images Upload */}
            <FormSection>
              <SwitchLabel sx={{ mb: 2 }}>Hero Images</SwitchLabel>
              <Box
                sx={(theme) => ({
                  border: `2px dashed ${theme.custom?.stroke || '#E0E0E0'}`,
                  borderRadius: '12px',
                  padding: 3,
                  backgroundColor: theme.custom?.background2 || '#F5F5F5',
                  minHeight: '120px',
                })}
              >
                {damageReportsHeroImages.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      marginBottom: 2,
                    }}
                  >
                    {damageReportsHeroImages.map((image, index) => (
                      <Box key={index} sx={{ position: 'relative' }}>
                        <Box
                          component="img"
                          src={`https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
                            image
                          )}`}
                          alt={`Hero ${index + 1}`}
                          sx={{
                            width: 180,
                            height: 120,
                            objectFit: 'cover',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeHeroImage(index)}
                          sx={(theme) => ({
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: theme.custom?.primary || '#FF6A00',
                            color: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            '&:hover': {
                              backgroundColor:
                                theme.custom?.primaryDark || '#E55A00',
                            },
                          })}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={(theme) => ({
                      textAlign: 'center',
                      padding: 3,
                      color: theme.custom?.textLight,
                    })}
                  >
                    <Typography
                      sx={(theme) => ({
                        fontSize: '0.9rem',
                        marginBottom: 1,
                        color: theme.custom?.textLight,
                      })}
                    >
                      No hero images uploaded yet
                    </Typography>
                  </Box>
                )}
                <Box sx={{ textAlign: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        uploadHeroImage(file);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="hero-image-upload"
                  />
                  <label htmlFor="hero-image-upload">
                    <MuiButton
                      variant="outlined"
                      component="span"
                      disabled={uploadingHeroImage}
                      startIcon={<Add />}
                    >
                      {uploadingHeroImage ? 'Uploading...' : 'Add Hero Image'}
                    </MuiButton>
                  </label>
                </Box>
              </Box>
            </FormSection>
          </Box>
        )}
      </FormSection>
      {/* Tab Order Configuration */}
      <FormSection sx={{ marginTop: 4 }}>
        <TabOrderConfig
          tabOrder={tabOrder}
          onChange={(newTabOrder: any) => setTabOrder(newTabOrder)}
        />
      </FormSection>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
        <SecondaryButton onClick={handleResetOrg} disabled={!hasChanged}>
          Discard Changes
        </SecondaryButton>
        <StyledButton
          onClick={handleSaveUpdates}
          disabled={!hasChanged || clusterValidationError}
        >
          Save
        </StyledButton>
      </Box>
    </Paper>
  );
}
